import React, { useState, useEffect } from "react";
import { useAxiosInstance } from "../services/Interceptor";
import {
  GetEventCombinePrice,
  GetEventsByEventId,
  SaveAppliedCouponCode,
} from "../services/EventsService";
import { getCouponPrice } from "../services/PublicServices";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setSubscriptionDetails,
  registerAccountStore,
} from "../store/slices/accountSlice";
import Nav from "../components/Nav";
import { PostPayment } from "../services/PaymentService";
import { registerAthleteWithEventId } from "../services/AthleteService";
import { useTrackEvent } from "../hooks/useTrackEvent";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

const getDefaultDiscountState = () => ({
  amount: 0,
  baseAmount: 0,
  percentage: 0,
  discountType: "",
  couponType: "",
});

export default function AthleteMembershipPlans() {
  const [combinePrice, setCombinePrice] = useState(0);
  const [positionDrillPrice, setPositionDrillPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPositionDrills, setHasPositionDrills] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({
    combine: true,
    positionDrill: false,
  });
  const [couponCode, setCouponCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(getDefaultDiscountState);
  const [couponError, setCouponError] = useState("");

  const [eventNameSelected, setEventNameSelected] = useState();
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const navigate = useNavigate();

  const trackEvent = useTrackEvent();

  const storedData = JSON.parse(
    localStorage.getItem("registeredAccount") || "{}",
  );
  const accounts = useSelector((state) => state.account.register);
  const user = accounts.length ? accounts[0] : storedData;

  useEffect(() => {
    if (!accounts.length && storedData) {
      dispatch(registerAccountStore(storedData));
    }
  }, [dispatch, accounts.length, storedData]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await GetEventsByEventId(apiInstance, user.eventId);
        if (data) {
          setEventNameSelected(`${data.date} - ${data.name} - ${data.city}`);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadEvents();
  }, [apiInstance, user.eventId]);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        setIsLoading(true);

        const combineData = await GetEventCombinePrice(
          apiInstance,
          user.eventId,
        );
        if (combineData.success) {
          setCombinePrice(combineData?.data?.combinePrice ?? 0);
          setHasPositionDrills(!!combineData?.data?.hasPositionDrills);
          setPositionDrillPrice(combineData?.data?.positionDrillPrice ?? 0);
          trackEvent("Registration3_ViewedPricing", {
            event_id: user.eventId,
            combine_price: combineData?.data?.combinePrice ?? 0,
            position_drill_price: combineData?.data?.positionDrillPrice ?? 0,
          });
        }
      } catch (error) {
        console.error("Error fetching pricing:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrices();
  }, [apiInstance, user.eventId, trackEvent]);

  useEffect(() => {
    const initial = {
      combine: true,
      positionDrill: false,
    };
    localStorage.setItem("subscriptionDetails", JSON.stringify(initial));
    localStorage.removeItem("appliedCouponDiscount");
    localStorage.removeItem("appliedCouponCode");
    localStorage.removeItem("appliedCouponType");
    // Reaching the plans screen means a single-combine purchase, so a bundle
    // accepted earlier and then abandoned must not be charged at payment.
    localStorage.removeItem("bundleDetails");
  }, []);

  // Invalidate coupon when plan selection changes and coupon no longer applies
  useEffect(() => {
    if (discountInfo.amount <= 0 || !discountInfo.couponType) return;

    if (
      discountInfo.couponType === "PositionDrills" &&
      !selectedOptions.positionDrill
    ) {
      setDiscountInfo(getDefaultDiscountState());
      setCouponError(
        "Selection changed — coupon removed because it no longer applies.",
      );
    } else if (discountInfo.couponType === "Combine" && combinePrice <= 0) {
      setDiscountInfo(getDefaultDiscountState());
      setCouponError(
        "Selection changed — coupon removed because it no longer applies.",
      );
    } else if (
      discountInfo.couponType === "CombineAndPositionDrills" &&
      !selectedOptions.positionDrill &&
      combinePrice <= 0
    ) {
      setDiscountInfo(getDefaultDiscountState());
      setCouponError(
        "Selection changed — coupon removed because it no longer applies.",
      );
    }
  }, [
    selectedOptions,
    combinePrice,
    discountInfo.amount,
    discountInfo.couponType,
  ]);

  // computeSelectedPrice subtracts any applied discount from the base total.
  // The discount is pre-validated to never exceed the eligible base amount,
  // so Math.max guards against floating-point edge cases only.
  const computeSelectedPrice = (opts) => {
    const base =
      (combinePrice ?? 0) +
      (hasPositionDrills && opts.positionDrill ? (positionDrillPrice ?? 0) : 0);
    return Math.max(base - Number(discountInfo.amount || 0), 0);
  };

  const couponNotApplicableMessage = (couponType) => {
    switch (couponType) {
      case "Combine":
        return "This coupon applies to Combine only. Please select a Combine Pass to use it.";
      case "PositionDrills":
        return "This coupon applies to Position Drills only. Please select Position Drills to use it.";
      case "CombineAndPositionDrills":
        return "This coupon applies to Combine and/or Position Drills. Please select at least one to use it.";
      default:
        return "This coupon is not applicable to your current selection.";
    }
  };

  const validateCouponApplicability = (couponType) => {
    if (couponType === "Combine") {
      if (combinePrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("Combine"),
        };
      }
      return { ok: true, eligibleAmount: combinePrice };
    }

    if (couponType === "PositionDrills") {
      if (!selectedOptions.positionDrill || positionDrillPrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("PositionDrills"),
        };
      }
      return { ok: true, eligibleAmount: positionDrillPrice };
    }

    if (couponType === "CombineAndPositionDrills") {
      const eligibleAmount =
        combinePrice + (selectedOptions.positionDrill ? positionDrillPrice : 0);
      if (eligibleAmount <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("CombineAndPositionDrills"),
        };
      }
      return { ok: true, eligibleAmount };
    }

    return {
      ok: false,
      eligibleAmount: 0,
      message: couponNotApplicableMessage(couponType),
    };
  };

  const applyCouponCode = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code.");
      return;
    }

    try {
      const response = await getCouponPrice(apiInstance, couponCode);

      const amount = Number(response?.amount ?? 0);
      const couponType = String(
        response?.type || response?.couponType || "",
      ).trim();
      const discountType = String(response?.discountType || "").trim();

      if (!amount || amount <= 0) {
        setCouponError("The coupon code is invalid or does not apply.");
        setDiscountInfo(getDefaultDiscountState());
        return;
      }

      if (!couponType) {
        setCouponError("Coupon type is missing. Please contact support.");
        setDiscountInfo(getDefaultDiscountState());
        return;
      }

      const { ok, eligibleAmount, message } =
        validateCouponApplicability(couponType);

      if (!ok) {
        setCouponError(
          message || "This coupon is not applicable to your current selection.",
        );
        setDiscountInfo(getDefaultDiscountState());
        return;
      }

      let appliedBaseAmount = eligibleAmount;
      let appliedDiscountAmount = 0;
      let appliedPercentage = 0;

      if (discountType === "Percent Off") {
        appliedPercentage = amount;
        appliedDiscountAmount = Math.min(
          (appliedBaseAmount * amount) / 100,
          appliedBaseAmount,
        );
      } else {
        appliedDiscountAmount = Math.min(amount, appliedBaseAmount);
      }

      const nextDiscountInfo = {
        amount: appliedDiscountAmount,
        baseAmount: appliedBaseAmount,
        percentage: appliedPercentage,
        discountType,
        couponType,
      };

      const isSameDiscount =
        Number(discountInfo.amount) === Number(nextDiscountInfo.amount) &&
        Number(discountInfo.baseAmount) ===
          Number(nextDiscountInfo.baseAmount) &&
        Number(discountInfo.percentage) ===
          Number(nextDiscountInfo.percentage) &&
        discountInfo.discountType === nextDiscountInfo.discountType &&
        discountInfo.couponType === nextDiscountInfo.couponType;

      if (isSameDiscount) {
        setCouponError("");
        return;
      }

      setDiscountInfo(nextDiscountInfo);
      setCouponError("");

      trackEvent("Registration3_5_AppliedCoupon", {
        event_id: user.eventId,
        coupon_code: couponCode,
        coupon_type: couponType,
        discount_amount: nextDiscountInfo.amount,
      });
    } catch (error) {
      console.error("Invalid coupon code", error);
      setCouponError("Invalid coupon code");
      setDiscountInfo(getDefaultDiscountState());
    }
  };

  const handleSelectOption = (option) => {
    if (option === "positionDrill" && !hasPositionDrills) return;

    const updated = { ...selectedOptions, [option]: !selectedOptions[option] };
    updated.combine = true;

    setSelectedOptions(updated);

    const selectedPrice = computeSelectedPrice(updated);

    dispatch(
      setSubscriptionDetails({
        subscriptionType: option,
        subscriptionPrice: selectedPrice,
      }),
    );

    localStorage.setItem("subscriptionDetails", JSON.stringify(updated));
  };

  const handleContinueToPayment = async () => {
    // computeSelectedPrice already applies the discount, so this price is the
    // actual amount the athlete owes. The free-registration branch below
    // triggers correctly when a coupon reduces the total to $0.
    const selectedPrice = computeSelectedPrice(selectedOptions);

    if (discountInfo.amount > 0) {
      localStorage.setItem(
        "appliedCouponDiscount",
        JSON.stringify(discountInfo),
      );
      localStorage.setItem("appliedCouponCode", couponCode);
      localStorage.setItem("appliedCouponType", discountInfo.couponType);
    } else {
      localStorage.removeItem("appliedCouponDiscount");
      localStorage.removeItem("appliedCouponCode");
      localStorage.removeItem("appliedCouponType");
    }

    if (selectedPrice <= 0) {
      const userAthlete = await registerAthleteWithEventId(
        apiInstance,
        user,
        user.eventId,
      );

      try {
        const paymentPayloads = [];

        if (hasPositionDrills && selectedOptions.positionDrill) {
          paymentPayloads.push({
            userId: userAthlete.data.id,
            amount: 0,
            isCombine: false,
            isPositionDrill: true,
            isAnnualSubscription: false,
            isMonthlySubscription: false,
            isCash: false,
            eventId: user.eventId,
            transactionId: "",
            intellipayToken: "",
          });
        }

        paymentPayloads.push({
          userId: userAthlete.data.id,
          amount: 0,
          isCombine: true,
          isPositionDrill: false,
          isAnnualSubscription: false,
          isMonthlySubscription: false,
          isCash: false,
          eventId: user.eventId,
          transactionId: "",
          intellipayToken: "",
        });

        let allSuccessful = true;

        for (const payload of paymentPayloads) {
          const paid = await PostPayment(apiInstance, payload);
          if (paid.status !== 200) {
            allSuccessful = false;
            toast.error("Payment after spot registration failed.", {
              position: "top-right",
            });
            break;
          }
        }

        if (allSuccessful) {
          if (couponCode && discountInfo.amount > 0) {
            try {
              await SaveAppliedCouponCode(apiInstance, couponCode);
            } catch (e) {
              console.error("Failed to save coupon code:", e);
            }
            localStorage.removeItem("appliedCouponCode");
            localStorage.removeItem("appliedCouponType");
            localStorage.removeItem("appliedCouponDiscount");
          }
          trackEvent("Registration5_RegistrationCompleted", {
            event_id: user.eventId,
            payment_method: "free",
          });
          toast.success("Athlete spot registration & payment successful!", {
            position: "top-right",
          });
          navigate("/register/registrationsuccess");
        } else {
          trackEvent("Registration4_5_PaymentFailed", {
            event_id: user.eventId,
            reason: "payment_status_not_200",
          });
        }
      } catch (err) {
        trackEvent("Registration4_5_PaymentFailed", {
          event_id: user.eventId,
          reason: err.message ?? "unknown",
        });
        console.error("Error during free registration:", err);
      }
    } else {
      window.location.href = "/register/subscription";
    }
  };

  const finalPrice = computeSelectedPrice(selectedOptions);

  return (
    <>
      <Nav />
      <div className="min-h-screen bg-[#363636] text-white w-full max-w-3xl mx-auto pt-16">
        <h2 className="text-center mb-2 text-white uppercase text-3xl font-black italic">
          Combine Registration
        </h2>
        <p className="text-gray-300 text-center my-0">
          Choose the perfect plan to level up your performance
        </p>

        <div className="text-red-500 text-center px-4 text-xl my-4 mb-6">
          {eventNameSelected}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20 ">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-4xl text-red-500"
            />{" "}
          </div>
        ) : (
          <>
            <div className="bg-[#1B1B1B] p-4 rounded-lg flex justify-between items-center mb-4 space-x-6 mx-4">
              <div className="flex items-center space-x-4 w-full">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="h-5 w-5 text-red-500 border-gray-700 rounded"
                />
                <img className="h-10" alt="Trackit" src="/combine.svg" />
                <div className="w-full">
                  <div className="flex justify-between items-center border-b border-gray-600 pb-2">
                    <span className="text-2xl font-semibold text-left">
                      Single Combine Pass
                    </span>
                    <span className="text-2xl font-semibold float-right">
                      ${combinePrice.toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-400">
                      <span className="font-semibold text-gray-200 pr-2">
                        What's included:
                      </span>
                      • Complete Combine following NFL scouting combine protocol
                      <br />• Access to your verified combine numbers
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {hasPositionDrills && (
              <div className="bg-[#1B1B1B] p-4 rounded-lg flex justify-between items-center mb-4 space-x-6 mx-4">
                <div className="flex items-center space-x-4 w-full">
                  <input
                    type="checkbox"
                    checked={selectedOptions.positionDrill}
                    onChange={() => handleSelectOption("positionDrill")}
                    className="h-5 w-5 text-red-500 border-gray-700 rounded cursor-pointer"
                  />
                  <img className="h-10" alt="Trackit" src="/combine.svg" />
                  <div className="w-full">
                    <div className="flex justify-between items-center border-b border-gray-600 pb-2">
                      <span className="text-2xl font-semibold text-left">
                        Position Drills at this combine
                      </span>
                      <span className="text-2xl font-semibold float-right">
                        ${positionDrillPrice.toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-400">&nbsp;</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-[#1B1B1B] p-4 rounded-lg mx-4 mb-4">
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="coupon-membership"
                  className="text-sm font-semibold text-gray-300 whitespace-nowrap"
                >
                  Coupon Code:
                </label>
                <input
                  type="text"
                  id="coupon-membership"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyCouponCode()}
                  placeholder="Enter coupon code"
                  className="flex-1 text-black px-2 py-1 rounded"
                />
                <button
                  onClick={applyCouponCode}
                  className="bg-white text-black px-3 py-1 rounded hover:bg-gray-200 whitespace-nowrap"
                >
                  Apply
                </button>
              </div>
              {couponError && (
                <p className="text-red-400 mt-2 text-sm">{couponError}</p>
              )}
              {discountInfo.amount > 0 && !couponError && (
                <p className="text-green-400 mt-2 text-sm">
                  Coupon applied! You save $
                  {Number(discountInfo.amount).toFixed(2)}.
                </p>
              )}
            </div>

            <div className="mt-4 rounded-lg flex justify-between items-center max-w-3xl mx-auto px-4">
              <div>
                <span className="text-sm font-normal text-red-500">
                  You selected:
                </span>
                <div className="flex flex-wrap gap-3 font-semibold">
                  <span>Single Combine Pass - ${combinePrice.toFixed(2)}</span>

                  {hasPositionDrills && selectedOptions.positionDrill && (
                    <span>
                      | Position Drills - ${positionDrillPrice.toFixed(2)}
                    </span>
                  )}
                </div>
                {discountInfo.amount > 0 && (
                  <>
                    {discountInfo.discountType === "Percent Off" && (
                      <div className="flex justify-between text-green-400 font-medium text-sm mt-2">
                        <span>Discount Percentage</span>
                        <span>
                          {Number(discountInfo.percentage || 0).toFixed(2)}%
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between text-green-400 font-medium text-sm">
                      <span>Discount Amount</span>
                      <span>- ${Number(discountInfo.amount).toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between font-bold text-white text-sm mt-1">
                      <span>Total</span>
                      <span>${finalPrice.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="mt-6 text-center">
                <button
                  className="bg-red-500 text-white uppercase rounded px-6 py-2 text-center hover:bg-red-700 cursor-pointer whitespace-nowrap"
                  onClick={handleContinueToPayment}
                >
                  PROCEED TO PAY
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
